import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { MarketingShell } from "@/components/landing/MarketingShell";
import { useCookie } from "@/components/useCookie";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { useRouter } from "next/router";
import { SeoHead } from "@/components/useSeo";

const Typography = ({
  variant,
  children,
  className
}: {
  variant: "h1" | "h2" | "h3" | "p";
  children: React.ReactNode;
  className?: string;
}) => {
  const variants = {
    h1: "text-3xl font-bold",
    h2: "text-2xl font-bold",
    h3: "text-xl font-bold",
    p: "text-base"
  };
  return <p className={`${className ?? ""} ${variants[variant]}`}>{children}</p>;
};

const PrivacyPolicy: React.FC = () => {
  const [cookieConsent, setCookieConsent, deleteCookieConsent] = useCookie("cookieConsent", {
    expires: 365
  });

  const router = useRouter();

  return (
    <MarketingShell>
      <SeoHead path="privacy" />
      <div className="container mx-auto px-4 py-8 mt-16">
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <Typography variant="p" className="mb-4">
              At DollarDeploy SaaS, we are committed to protecting your privacy and ensuring
              the security of your personal information. This Privacy Policy outlines our
              practices concerning the collection, use, and protection of your data.
            </Typography>

            {process.env.NEXT_PUBLIC_ANALYTICS_CONSENT === "1" &&
              cookieConsent !== undefined && (
                <div className="flex flex-col gap-2 p-4 rounded-md border mb-4 mt-4">
                  <h1 className="text-lg font-bold">Your Cookie Consent Status</h1>

                  <div>
                    {cookieConsent === "false"
                      ? "Declined"
                      : cookieConsent === undefined
                        ? "Not set"
                        : "Accepted on " + new Date(cookieConsent).toLocaleString()}
                  </div>

                  <div className="flex gap-2 items-center">
                    <Button
                      onClick={() => {
                        setCookieConsent(new Date().toISOString());
                        router.reload();
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      onClick={() => {
                        setCookieConsent("false");
                        router.reload();
                      }}
                    >
                      Decline
                    </Button>
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          aria-haspopup="true"
                          size="icon"
                          variant="secondary"
                          className="h-8"
                          animations="none"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Toggle menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => deleteCookieConsent()}>
                          Delete Cookie Consent
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )}

            <Typography variant="h3" className="mb-2">
              1. Data Controller
            </Typography>

            <Typography variant="p" className="mb-8">
              DollarDeploy is a software-as-a-service (SaaS) product operated by Wizecore Oy, a
              company registered in Finland. For the purposes of the General Data Protection
              Regulation (EU) 2016/679 (“GDPR”), Wizecore Oy acts as the data controller for
              all personal data processed in connection with the DollarDeploy service.
              <br />
              <br />
              Company details:
              <ul className="list-disc list-inside mb-4">
                <li>Legal name: Wizecore Oy</li>
                <li>Business ID (Y-tunnus): 3322522-2</li>
                <li>Registered address: Eerikinkatu 28, 00180 Helsinki, Finland</li>
                <li>
                  Email:{" "}
                  <a
                    href="mailto:privacy@dollardeploy.com"
                    className="text-primary hover:underline"
                  >
                    privacy@dollardeploy.com
                  </a>
                </li>
              </ul>
            </Typography>

            <Typography variant="h3" className="mb-2">
              2. When Do We Collect Personal Data
            </Typography>
            <Typography variant="p" className="mb-8">
              We collect personal data when you:
              <ul className="list-disc list-inside mb-4">
                <li>create an account or use our services</li>
                <li>interact with our website or application</li>
                <li>contact us for customer support or other inquiries</li>
                <li>subscribe to product updates or marketing communications</li>
                <li>make payments or manage billing through our services</li>
              </ul>
            </Typography>

            <Typography variant="h3" className="mb-2">
              3. Categories of Personal Data We Process
            </Typography>
            <Typography variant="p" className="mb-8">
              Depending on your use of our services, we may process the following categories of
              personal data:
              <ul className="list-disc list-inside mb-4">
                <li>Account and contact data: name, email address, company name, user role</li>
                <li>
                  Service usage data: IP address, log files, timestamps, device and browser
                  information
                </li>
                <li>
                  Billing data: billing contact details, invoices, transaction references
                  (payment card data is processed by authorized payment service providers and
                  is not stored by us)
                </li>
                <li>Communications: customer support requests and correspondence</li>
                <li>Cookies and similar technologies: as described in our Cookie Policy</li>
              </ul>
            </Typography>

            <Typography variant="h3" className="mb-2">
              4. Purposes and Legal Bases for Processing
            </Typography>
            <Typography variant="p" className="mb-8">
              We process personal data only where permitted by GDPR, for the following
              purposes:
              <div className="overflow-x-auto mt-4">
                <table className="w-full border-collapse mb-8">
                  <thead>
                    <tr className="border-b-4 border-secondary">
                      <th className="text-left py-3 px-2 md:px-4 font-bold">Purpose</th>
                      <th className="text-left py-3 px-2 md:px-4 font-bold">
                        Legal Basis (GDPR Art. 6)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-secondary">
                      <td className="py-3 px-2 md:px-4">
                        Providing and operating the service
                      </td>
                      <td className="py-3 px-2 md:px-4">Performance of a contract</td>
                    </tr>
                    <tr className="border-b border-secondary">
                      <td className="py-3 px-2 md:px-4">
                        User account management and customer support
                      </td>
                      <td className="py-3 px-2 md:px-4">Performance of a contract</td>
                    </tr>
                    <tr className="border-b border-secondary">
                      <td className="py-3 px-2 md:px-4">Billing and accounting</td>
                      <td className="py-3 px-2 md:px-4">Compliance with a legal obligation</td>
                    </tr>
                    <tr className="border-b border-secondary">
                      <td className="py-3 px-2 md:px-4">
                        Service security, reliability, and improvement
                      </td>
                      <td className="py-3 px-2 md:px-4">Legitimate interest</td>
                    </tr>
                    <tr className="border-b border-secondary">
                      <td className="py-3 px-2 md:px-4">
                        Marketing and promotional communications
                      </td>
                      <td className="py-3 px-2 md:px-4">Consent</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              Billing and accounting data is processed and retained in accordance with
              applicable laws, including the Finnish Accounting Act (Kirjanpitolaki 1336/1997).
              <br />
              <br />
              You may withdraw your consent at any time by using the unsubscribe link in our
              communications or by contacting us.
            </Typography>

            <Typography variant="h3" className="mb-2">
              5. Data Sharing and Processors
            </Typography>
            <Typography variant="p" className="mb-8">
              We may share personal data with trusted third-party service providers who act as
              data processors on our behalf, including:
              <ul className="list-disc list-inside mb-4">
                <li>cloud hosting and infrastructure providers</li>
                <li>payment service providers</li>
                <li>email and customer support services</li>
                <li>monitoring and security service providers</li>
              </ul>
              All processors are bound by data processing agreements and process personal data
              in accordance with GDPR and applicable data protection laws.
            </Typography>

            <Typography variant="h3" className="mb-2">
              6. International Data Transfers
            </Typography>
            <Typography variant="p" className="mb-8">
              Personal data is primarily processed within the European Union. Where personal
              data is transferred outside the EU/EEA, we ensure appropriate safeguards are in
              place, such as Standard Contractual Clauses approved by the European Commission.
            </Typography>

            <Typography variant="h3" className="mb-2">
              7. Data Retention
            </Typography>
            <Typography variant="p" className="mb-8">
              We retain personal data only for as long as necessary to fulfill the purposes
              described in this Privacy Policy, including compliance with legal, accounting,
              and regulatory obligations. Retention periods vary depending on the nature of the
              data and the purpose of processing.
            </Typography>

            <Typography variant="h3" className="mb-2">
              8. Data Security
            </Typography>
            <Typography variant="p" className="mb-8">
              We implement appropriate technical and organizational measures to protect
              personal data against unauthorized access, loss, alteration, or misuse.
            </Typography>

            <Typography variant="h3" className="mb-2">
              9. Your Rights Under GDPR
            </Typography>
            <Typography variant="p" className="mb-8">
              We fully comply with the General Data Protection Regulation (GDPR). You have the
              right to:
              <ul className="list-disc list-inside mb-4">
                <li>access your personal data</li>
                <li>request rectification of inaccurate data</li>
                <li>request erasure of your data</li>
                <li>restrict the processing of your data</li>
                <li>
                  receive your data in a structured, commonly used, and machine-readable format
                  (data portability)
                </li>
                <li>object to the processing of your data</li>
                <li>withdraw your consent at any time</li>
              </ul>
              You also have the right to lodge a complaint with a supervisory authority.
            </Typography>

            <Typography variant="h3" className="mb-2">
              10. Cookies
            </Typography>
            <Typography variant="p" className="mb-8">
              We use cookies and similar technologies to ensure the functionality, security,
              and usability of our website and services. Further information about cookies is
              available in our Cookie Policy.
            </Typography>

            <Typography variant="h3" className="mb-2">
              11. Changes to This Policy
            </Typography>
            <Typography variant="p" className="mb-8">
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy on this page.
              <br />
              <br />
              <strong>Last updated:</strong> January 2025
            </Typography>

            <Typography variant="h3" className="mb-2">
              12. Contact
            </Typography>
            <Typography variant="p" className="mb-8">
              If you have any questions about this Privacy Policy or our data protection
              practices, please contact us at:{" "}
              <a
                href="mailto:privacy@dollardeploy.com"
                className="text-primary hover:underline"
              >
                privacy@dollardeploy.com
              </a>
            </Typography>
          </CardContent>
        </Card>
      </div>
    </MarketingShell>
  );
};

export default PrivacyPolicy;
